// Espejo en myklubb-torneos (src/legal/render.js): si cambias este archivo, cambia la copia y su prueba.
//
// Render PURO de los documentos legales: rellena los marcadores de `./documents` con los
// datos del club o de la plataforma. Solo importa de `./documents` (nada de Supabase), para
// que Torneos lo copie byte a byte. `src/lib/services/legal.js` lo reexporta con los mismos
// nombres.
import { LEGAL_DOC_KEYS, getLegalDocDefinition } from './documents'

const FALTA = '[por definir]' // placeholder visible cuando el dato legal aún no se captura

const reemplazar = (contenido, token, valor) => contenido.split(token).join(valor)

const rellenar = (contenido, valores = {}) =>
  Object.entries(valores).reduce(
    (texto, [token, valor]) =>
      reemplazar(texto, token, valor && String(valor).trim() ? valor : FALTA),
    contenido
  )

// Cláusulas que solo aplican a algunos casos (hoy, el RFC de MYKLUBB): si no aplica, el token
// desaparece sin dejar rastro. NO pasan por `rellenar`, que convertiría el vacío en
// "[por definir]" -- un dato faltante y una cláusula que no aplica no son lo mismo.
const rellenarOpcionales = (contenido, clausulas = {}) =>
  Object.entries(clausulas).reduce(
    (texto, [token, valor]) => reemplazar(texto, token, valor || ''),
    contenido
  )

// Devuelve { docKey, version, titulo, contenido } con los placeholders rellenados.
export const renderLegalDoc = (docKey, valores = {}, clausulas = {}) => {
  const def = getLegalDocDefinition(docKey)
  if (!def) return null
  return { ...def, contenido: rellenarOpcionales(rellenar(def.contenido, valores), clausulas) }
}

// Aviso del club (responsable). Rellena con los datos legales de la organizacion. Desde la 1.1
// (pieza 2b) no tiene clausulas condicionales: lo que se comparte con la organizadora de un
// torneo es fijo y el texto lo dice siempre. Un segundo argumento (la configuracion de
// comparticion de antes) se ignora.
export const renderAvisoClub = (org = {}) =>
  renderLegalDoc(LEGAL_DOC_KEYS.AVISO_CLUB, {
    '[NOMBRE DEL CLUB]': org.name,
    '[RAZÓN SOCIAL]': org.razon_social,
    '[DOMICILIO]': org.domicilio_fiscal,
    '[CORREO ARCO]': org.correo_privacidad,
    '[FECHA]': new Date().toLocaleDateString('es-MX'),
  })

// Aviso de MYKLUBB (responsable de la cuenta). Rellena con platform_config.legal_entity.
export const renderAvisoMyklubb = (legal = {}) =>
  renderLegalDoc(LEGAL_DOC_KEYS.AVISO_MYKLUBB, {
    '[RAZÓN SOCIAL DE MYKLUBB]': legal.razon_social,
    '[DOMICILIO]': legal.domicilio,
    '[CORREO ARCO]': legal.correo_privacidad,
    '[FECHA]': new Date().toLocaleDateString('es-MX'),
  })

// DPA MYKLUBB <-> Club (la puerta legal del Club y el enlace del club sin MYKLUBB). Desde la
// 1.1 su §7 (comparticion con organizadores) es obligatoria y fija: un segundo argumento se
// ignora.
export const renderDpa = (legal = {}) => {
  const def = getLegalDocDefinition(LEGAL_DOC_KEYS.DPA)
  return renderLegalDoc(LEGAL_DOC_KEYS.DPA, {
    '[RAZÓN SOCIAL DE MYKLUBB]': legal.razon_social,
    '[FECHA/VERSIÓN]': def ? def.version : '',
  })
}

// Declaracion del club sin MYKLUBB (la acepta el contacto en el enlace de captura).
export const renderDeclaracionClubSinMyklubb = ({ club, torneo } = {}) => {
  const def = getLegalDocDefinition(LEGAL_DOC_KEYS.DECLARACION_CLUB_SIN_MYKLUBB)
  return renderLegalDoc(LEGAL_DOC_KEYS.DECLARACION_CLUB_SIN_MYKLUBB, {
    '[NOMBRE DEL CLUB]': club,
    '[TORNEO]': torneo,
    '[FECHA/VERSIÓN]': def ? def.version : '',
  })
}

// Contrato de Servicio (SaaS) MYKLUBB <-> Club.
// El RFC es opcional: si no está, la cláusula lo omite en vez de dejar un
// "[por definir]" en el documento que el club acepta.
export const renderTosSaas = (legal = {}) => {
  const def = getLegalDocDefinition(LEGAL_DOC_KEYS.TOS_SAAS)
  const rfc = String(legal.rfc || '').trim()
  return renderLegalDoc(
    LEGAL_DOC_KEYS.TOS_SAAS,
    {
      '[RAZÓN SOCIAL DE MYKLUBB]': legal.razon_social,
      '[DOMICILIO]': legal.domicilio,
      '[CORREO DE CONTACTO]': legal.correo_contacto,
      '[CIUDAD/ESTADO PARA JURISDICCIÓN]': legal.ciudad_jurisdiccion,
      '[FECHA/VERSIÓN]': def ? def.version : '',
    },
    {
      '[RFC PROVEEDOR]': rfc ? ` y RFC ${rfc}` : '',
      '[RFC PARTES]': rfc ? ` — RFC ${rfc}` : '',
    }
  )
}

// Render de un documento legal de plataforma por su clave (DPA / Contrato). Un tercer
// argumento (la configuracion de comparticion de antes) se ignora.
export const renderDocRequerido = (docKey, legal = {}) => {
  if (docKey === LEGAL_DOC_KEYS.TOS_SAAS) return renderTosSaas(legal)
  return renderDpa(legal)
}
